// API実行
import axios from "axios";
// csvダウンロード
import Papa from "papaparse";
import encoding from "encoding-japanese";

export default function useCommonMethods() {
  /**
   * 在庫金額一覧を在庫ID（降順）,仕入日（降順）でソートする
   * @type {array} - pastInventories 過去の在庫一覧
   * @return {array} - pastInventories 過去の在庫一覧（ソート済み）
   */
  function sort(zaicoMoneyList) {
    return zaicoMoneyList.sort(function (a, b) {
      // 在庫IDで並び替え
      if (a.id !== b.id) {
        return b.id - a.id;
      }
      // 仕入日で並び替え
      if (a["仕入日"] !== b["仕入日"]) {
        return new Date(b["仕入日"]) - new Date(a["仕入日"]);
      }
      return 0;
    });
  }

  /**
   * 仕入明細一覧jsonに変更履歴をマージする
   */
  function mergePurchasesItemsAndHistories(purchase_items, histories_except_purchase) {
    histories_except_purchase.forEach((history) => {
      const inventory_id = history.inventory_id;
      if (!purchase_items[inventory_id]) {
        purchase_items[inventory_id] = [];
      }
      const history_purchase = {
        purchase_id: null,
        purchase_date: history["operation_datetime"],
        quantity: history["quantity_diff"],
        unit_price: null,
      };
      purchase_items[inventory_id] = purchase_items[inventory_id].concat(history_purchase);
    });
    return purchase_items;
  }

  /**
   * 出庫明細一覧jsonに変更履歴をマージする
   */
  function mergeDeliveriesAndHistories(deliveries, histories_except_delivery) {
    histories_except_delivery.forEach((history) => {
      const inventory_id = history.inventory_id;
      if (!deliveries[inventory_id]) {
        deliveries[inventory_id] = [];
      }
      const history_delivery = {
        delivery_date: history["operation_datetime"],
        quantity: Number(history["quantity_diff"]) * -1,
      };
      deliveries[inventory_id] = deliveries[inventory_id].concat(history_delivery);
    });
    return deliveries;
  }

  // 期間内のデータだけにフィルタ
  function filterPurchasesItems(purchasesItemsAndHistories, dateFrom, dateTo) {
    let purchasesItems = {};
    for (let inventory_id in purchasesItemsAndHistories) {
      purchasesItems[inventory_id] = purchasesItemsAndHistories[inventory_id].filter((purchase_item) => {
        return (
          new Date(dateFrom) < new Date(purchase_item.purchase_date) &&
          new Date(purchase_item.purchase_date) <= new Date(dateTo)
        );
      });
    }
    return purchasesItems;
  }

  /**
   * 入庫IDで新旧を比較する（入庫IDが大きいほど新しい）
   * 入庫日が同じロットが複数ある場合のタイブレークに使う。
   * 変更履歴由来のデータ（purchase_idがnull）は入庫データより古い扱いにする。
   * @return {number} - 正: aが古い、負: aが新しい、0: 判定不能
   */
  function comparePurchaseIdAsc(a_purchase_id, b_purchase_id) {
    const a = a_purchase_id == null ? null : Number(a_purchase_id);
    const b = b_purchase_id == null ? null : Number(b_purchase_id);
    if (a === null && b === null) return 0;
    if (a === null) return -1;
    if (b === null) return 1;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }

  /**
   * 仕入明細データの入庫日と変更履歴の操作日時がごっちゃになってるので入庫日が新しい順にソートする
   * 入庫日が同じ場合は入庫IDが大きい（後から登録した）ものを新しいとみなす。
   * ※ TowDays.vue の sortByPurchaseDate（昇順）と同じ規則で並べること
   */
  function sortPurchasesItems(purchasesItemsAndHistories) {
    for (let inventory_id in purchasesItemsAndHistories) {
      purchasesItemsAndHistories[inventory_id] = purchasesItemsAndHistories[inventory_id].sort((a, b) => {
        if (a.purchase_date > b.purchase_date) return -1;
        if (a.purchase_date < b.purchase_date) return 1;
        return comparePurchaseIdAsc(b.purchase_id, a.purchase_id);
      });
    }
    return purchasesItemsAndHistories;
  }

  // 期間内のデータだけにフィルタ
  function filterDeliveries(deliveriesAndHistories, dateFrom, dateTo) {
    let deliveries = {};
    for (let inventory_id in deliveriesAndHistories) {
      deliveries[inventory_id] = deliveriesAndHistories[inventory_id].filter((delivery) => {
        return (
          new Date(dateFrom) < new Date(delivery.delivery_date) && new Date(delivery.delivery_date) <= new Date(dateTo)
        );
      });
    }
    return deliveries;
  }

  /**
   * 出庫明細データの入庫日と変更履歴の操作日時がごっちゃになってるので出庫日が新しい順にソートする
   */
  function sortDeliveries(deliveriesAndHistories) {
    for (let inventory_id in deliveriesAndHistories) {
      deliveriesAndHistories[inventory_id] = deliveriesAndHistories[inventory_id].sort((a, b) => {
        if (a.delivery_date > b.delivery_date) return -1;
        if (a.delivery_date < b.delivery_date) return 1;
        return 0;
      });
    }
    return deliveriesAndHistories;
  }

  /**
   * 先入先出法でdateFrom日時点の在庫金額のjsonを作成する。
   */
  function createZaicoMoneyJson(
    inventories, 
    purchases, 
    purchasesItemsSorted, 
    dateFrom, 
    is_only_purchase_and_packing_slip,
    inventory_unit_price_json
  ) {
    console.log("start createZaicoMoneyJson");
    let zaicoMoneyJson = {};
    inventories.forEach((inventory) => {
      zaicoMoneyJson[inventory["id"]] = [];

      let quantity = Number(inventory.quantity);

      // 在庫数量が0以下
      if (quantity <= 0) {
        let zaico_money = JSON.parse(JSON.stringify(inventory));
        zaico_money[dateFrom + "の数量"] = quantity;
        zaico_money["仕入単価"] = null;
        zaico_money["仕入日"] = "";
        zaico_money["取引先"] = "";
        delete zaico_money["quantity"];
        zaicoMoneyJson[zaico_money["id"]].push(zaico_money);
        return;
      }

      // 仕入明細データも変更履歴も存在しない在庫
      if (!purchasesItemsSorted[inventory["id"]]) {
        let zaico_money = JSON.parse(JSON.stringify(inventory));
        zaico_money[dateFrom + "の数量"] = quantity;
        zaico_money["仕入単価"] = inventory_unit_price_json[inventory["id"]] || null;
        zaico_money["仕入日"] = "";
        zaico_money["取引先"] = "";

        delete zaico_money["quantity"];
        zaicoMoneyJson[zaico_money["id"]].push(zaico_money);
        this.hasNoPriceData = true;
        return;
      }

      // 仕入明細データか変更履歴が存在する在庫
      // 直近の入庫から割り当てていく
      let remainingQuantity = quantity;
      purchasesItemsSorted[inventory["id"]].forEach((purchase_item) => {
        if (remainingQuantity <= 0) return;

        let zaico_money = JSON.parse(JSON.stringify(inventory));
        zaico_money[dateFrom + "の数量"] = Math.min(remainingQuantity, purchase_item["quantity"]);
        zaico_money["仕入単価"] = purchase_item["unit_price"] || inventory_unit_price_json[inventory["id"]] || null;
        zaico_money["仕入日"] = purchase_item["purchase_date"];
        // 同じ仕入日のロットを先入先出で引き当てる際のタイブレーク用（出力前に削除する）
        zaico_money["_purchase_id"] = purchase_item.purchase_id == null ? null : purchase_item.purchase_id;
        let purchase = purchases[purchase_item.purchase_id];
        zaico_money["取引先"] = purchase ? purchase.customer_name : "";
        remainingQuantity -= Number(purchase_item.quantity);
        delete zaico_money["quantity"];
        zaicoMoneyJson[zaico_money["id"]].push(zaico_money);
      });

      // 割り当てできなかった分は新規作成時の数量ということ
      // 新規作成時の数量は変更履歴では取得できていないので
      if (remainingQuantity > 0 && !is_only_purchase_and_packing_slip) {
        let zaico_money = JSON.parse(JSON.stringify(inventory));
        zaico_money[dateFrom + "の数量"] = remainingQuantity;
        zaico_money["仕入単価"] = inventory_unit_price_json[inventory["id"]] || null;
        zaico_money["仕入日"] = "";
        zaico_money["取引先"] = "";
        delete zaico_money["quantity"];
        zaicoMoneyJson[zaico_money["id"]].push(zaico_money);
      }
    });

    console.log("end createZaicoMoneyJson");
    return zaicoMoneyJson;
  }

  /**
   * 移動平均法でdateFrom日時点の在庫金額のjsonを作成する。
   */
  function createZaicoMoneyJsonMovingAverage(inventories, purchasesItemsAndDeliveriesSorted, dateFrom, inventory_unit_price_json) {
    console.log("start createZaicoMoneyJson");
    let zaicoMoneyJson = {};
    inventories.forEach((inventory) => {
      zaicoMoneyJson[inventory["id"]] = [];

      const quantity = Number(inventory.quantity);

      // 在庫数量が0以下
      if (quantity <= 0) {
        let zaico_money = JSON.parse(JSON.stringify(inventory));
        zaico_money[dateFrom + "の数量"] = quantity;
        zaico_money["仕入単価"] = null;
        delete zaico_money["quantity"];
        zaicoMoneyJson[zaico_money["id"]].push(zaico_money);
        return;
      }

      // 仕入明細データも変更履歴も存在しない在庫
      if (!purchasesItemsAndDeliveriesSorted[inventory["id"]]) {
        let zaico_money = JSON.parse(JSON.stringify(inventory));
        zaico_money[dateFrom + "の数量"] = quantity;
        zaico_money["仕入単価"] = null;
        delete zaico_money["quantity"];
        zaicoMoneyJson[zaico_money["id"]].push(zaico_money);
        this.hasNoPriceData = true;
        return;
      }

      // 仕入明細データか変更履歴が存在する在庫
      // 古い入庫から計算していく
      let remainingQuantity = 0;
      let movingAverageUnitPrice = 0;

      // 新規在庫作成時の数量がある場合
      const quantity_of_inventory_created = this.calc_quantity_of_inventory_created(
        purchasesItemsAndDeliveriesSorted[inventory["id"]],
        quantity
      );
      if (quantity_of_inventory_created > 0) {
        remainingQuantity = quantity_of_inventory_created;
        movingAverageUnitPrice = null;
        if (inventory_unit_price_json[inventory["id"]] != null) {
          movingAverageUnitPrice = Number(inventory_unit_price_json[inventory["id"]]);
        };
      }

      purchasesItemsAndDeliveriesSorted[inventory["id"]].forEach((history) => {
        if (history.purchase_date) {
          let unit_price = history.unit_price;
          if (unit_price == null && inventory_unit_price_json[inventory["id"]] != null) {
            unit_price = inventory_unit_price_json[inventory["id"]];
          }
          if (movingAverageUnitPrice != null && unit_price != null && history.quantity) {
            movingAverageUnitPrice =
              (movingAverageUnitPrice * remainingQuantity + Number(history.quantity) * Number(unit_price)) /
              (remainingQuantity + Number(history.quantity));
          } else {
            movingAverageUnitPrice = null;
          }
          remainingQuantity += Number(history.quantity);
        } else if (history.delivery_date) {
          remainingQuantity -= Number(history.quantity);
          if (remainingQuantity == 0) {
            movingAverageUnitPrice = 0;
          }
        } else {
          console.error("historyにdelivery_dateもpurchase_dateもないエラー！");
          console.error("history: ", history);
        }
      });

      let zaico_money = JSON.parse(JSON.stringify(inventory));
      zaico_money[dateFrom + "の数量"] = quantity;
      zaico_money["仕入単価"] = movingAverageUnitPrice != null ? movingAverageUnitPrice.toFixed(4) : null;
      delete zaico_money["quantity"];
      zaicoMoneyJson[zaico_money["id"]].push(zaico_money);
    });

    console.log("end createZaicoMoneyJson");
    return zaicoMoneyJson;
  }

  /**
   * 新規在庫作成時の数量の計算
   */
  function calc_quantity_of_inventory_created(purchasesItemsAndDeliveriesSortedInventory, quantity) {
    let quantity_of_inventory_created = 0;
    purchasesItemsAndDeliveriesSortedInventory.forEach((history) => {
      if (history.purchase_date) {
        quantity_of_inventory_created += Number(history.quantity) * 10000;
      } else if (history.delivery_date) {
        quantity_of_inventory_created -= Number(history.quantity) * 10000;
      }
    });
    return (quantity * 10000 - quantity_of_inventory_created) / 10000;
  }

  /**
   * APIから変更履歴一覧を取得する
   * @type {function}
   * @param {string} - start_date 例：2021/01/01
   * @param {string} - end_date 例：2021/02/01
   * @param {string} - quantity_diff_option 例：plus
   * @return {json} - histories 変更履歴一覧
   */
  async function loopFetchHistories(start_date, end_date, quantity_diff_option) {
    let histories = [];
    let pageToken = "";
    let page = 1;
    do {
      console.time("fetchHistories_" + quantity_diff_option);
      const res = await this.fetchHistories(this.accessToken, pageToken, start_date, end_date, quantity_diff_option);
      console.timeEnd("fetchHistories_" + quantity_diff_option);
      console.log("[" + page + "]変更履歴APIのレスポンス");
      console.log(res.data);
      if (res.data.code != 200) {
        this.showError(res.data.message);
        return {};
      }
      // マージ
      histories = histories.concat(res.data.data);
      pageToken = res.data.nextPageToken;
      page++;
    } while (pageToken);
    console.log("マージされた変更履歴一覧");
    console.log(JSON.parse(JSON.stringify(histories)));
    return histories;
  }

  // 入庫データはないが変化数量がプラスの変更履歴
  function fillter_histories_plus(histories, dateFrom, is_exclude_cancel_data = false) {
    let result = [];
    histories.forEach((history) => {
      if (hasValidPurchaseData(history)) return;
      if (hasValidDeliveryData(history)) return;
      if (history["operation_note"].includes("取消済") || history["operation_note"].includes("Canceled")) return;
      if (
        is_exclude_cancel_data &&
        (history["operation_type"].includes("取消") || history["operation_type"].includes("Cancel"))
      )
        return;
      const quantity_diff = Number(history["quantity_diff"]);
      if (quantity_diff <= 0) return;
      const operation_datetime = history["operation_datetime"].substring(0, 10);
      if (new Date(dateFrom) < new Date(operation_datetime)) return;
      result.push({
        inventory_id: history["inventory_id"],
        operation_datetime: operation_datetime,
        quantity_diff: quantity_diff,
      });
    });
    return result;
  }

  // 出庫データはないが変化数量がマイナスの変更履歴
  function fillter_histories_minus(histories, dateFrom, is_exclude_cancel_data = false) {
    let result = [];
    histories.forEach((history) => {
      if (hasValidPurchaseData(history)) return;
      if (hasValidDeliveryData(history)) return;
      if (history["operation_note"].includes("取消済") || history["operation_note"].includes("Canceled")) return;
      if (
        is_exclude_cancel_data &&
        (history["operation_type"].includes("取消") || history["operation_type"].includes("Cancel"))
      )
        return;
      const quantity_diff = Number(history["quantity_diff"]);
      if (quantity_diff >= 0) return;
      const operation_datetime = history["operation_datetime"].substring(0, 10);
      if (new Date(dateFrom) < new Date(operation_datetime)) return;
      result.push({
        inventory_id: history["inventory_id"],
        operation_datetime: operation_datetime,
        quantity_diff: quantity_diff,
      });
    });
    return result;
  }

  // キャンセルされた変更履歴のうち、在庫数の再計算の対象
  function fillter_histories_canceled(histories, dateFrom) {
    let result = {};
    histories.forEach((history) => {
      const quantity_diff = Number(history["quantity_diff"]);
      if (quantity_diff == 0) return;

      // 赤データ
      const is_cancel_data = history["operation_type"].includes("取消") || history["operation_type"].includes("Cancel");
      if (!is_cancel_data) return;

      const inventory_id = history["inventory_id"];

      // 指定日以前にキャンセルしてたら再計算は不要
      const operation_datetime_of_cancel = new Date(history["operation_datetime"].substring(0, 10));
      if (operation_datetime_of_cancel <= dateFrom) return;

      let operation_datetime_of_canceled;
      if (hasValidPurchaseData(history)) {
        const purchase_id = history["operation_content"]["purchase"]["id"];
        const canceled_data = histories.find((h) => {
          return is_canceled_purchase(h, inventory_id, purchase_id);
        });
        if (!canceled_data) {
          console.error("キャンセルされた入庫データが見つからないエラー");
          console.error("history: ", history);
          return;
        }
        operation_datetime_of_canceled = new Date(canceled_data["operation_datetime"].substring(0, 10));
      } else if (hasValidDeliveryData(history)) {
        const packing_slip_id = history["operation_content"]["packing_slip"]["id"];
        const canceled_data = histories.find((h) => {
          return is_canceled_packingslip(h, inventory_id, packing_slip_id);
        });
        if (!canceled_data) {
          console.error("キャンセルされた出庫データが見つからないエラー");
          console.error("history: ", history);
          return;
        }
        operation_datetime_of_canceled = new Date(canceled_data["operation_datetime"].substring(0, 10));
      } else {
        operation_datetime_of_canceled = new Date(history["operation_note"].substring(0, 10));
      }

      // 指定日をまたいでいる場合は再計算の対象
      if (operation_datetime_of_canceled <= dateFrom) {
        if (!result[inventory_id]) {
          result[inventory_id] = [];
        }
        result[inventory_id].push({
          operation_datetime: history["operation_datetime"],
          quantity_diff: quantity_diff,
        });
      }
    });
    return result;
  }

  // キャンセルされた入庫データかどうか
  function is_canceled_purchase(history, inventory_id, purchase_id) {
    // ステージングのスナのアカウントで、なぜか取消済の文字が出ていないが、実際には取消済みなので強制的にtrueにする
    // 詳細スレッド：https://zaico.slack.com/archives/C06TTBQM15H/p1728439640499279
    if (
      history["operation_datetime"] == "2023-11-06T11:24:04+09:00" &&
      inventory_id == 2081552 &&
      purchase_id == 2998
    ) {
      return true;
    }
    if (!hasValidPurchaseData(history)) return false;
    if (history["operation_type"].includes("入庫") || history["operation_type"].includes("Received")) {
      if (history["operation_note"].includes("取消済") || history["operation_note"].includes("Canceled")) {
        if (history["inventory_id"] == inventory_id) {
          if (history["operation_content"]["purchase"]["id"] == purchase_id) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // キャンセルされた出庫データかどうか
  function is_canceled_packingslip(history, inventory_id, packing_slip_id) {
    if (!hasValidDeliveryData(history)) return false;
    if (history["operation_type"].includes("出庫") || history["operation_type"].includes("Completed Delivery")) {
      if (history["operation_note"].includes("取消済") || history["operation_note"].includes("Canceled")) {
        if (history["inventory_id"] == inventory_id) {
          if (history["operation_content"]["packing_slip"]["id"] == packing_slip_id) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // 入庫データがある
  function hasValidPurchaseData(history) {
    const operationContent = history["operation_content"];
    return (
      operationContent &&
      Object.keys(operationContent).length > 0 &&
      operationContent["purchase"] &&
      operationContent["purchase"]["id"]
    );
  }
  // 出庫データがある
  function hasValidDeliveryData(history) {
    const operationContent = history["operation_content"];
    return (
      operationContent &&
      Object.keys(operationContent).length > 0 &&
      operationContent["packing_slip"] &&
      operationContent["packing_slip"]["id"]
    );
  }
  /**
   * APIから変更履歴一覧を取得する
   */
  async function fetchHistories(accessToken, pageToken, start_date, end_date, quantity_diff_option) {
    try {
      let _params = {
        accessToken: accessToken,
        pageToken: pageToken,
        end_date: end_date,
        quantity_diff_option: quantity_diff_option,
      };
      if (start_date) _params["start_date"] = start_date;
      return await axios.get("/inventory_histories", {
        params: _params,
      });
    } catch (e) {
      console.log("変更履歴API　でエラー");
      console.log(e);
      let message = e.toString();
      if (message.match(/Network Error/)) {
        message = "TimeOut Error";
      }
      return {
        data: {
          code: -1,
          message: message,
        },
      };
    }
  }

  /**
   * APIから入庫を全件取得する
   * @type {function}
   * @return {json} - purchases 入庫一覧
   */
  async function loopFetchPurchases() {
    let result = {};
    let page = 1;
    let lastPage = 1;
    while (page <= lastPage) {
      const res = await this.fetchPurchases(page);
      console.log("[" + page + "]入庫一覧APIのレスポンス");
      console.log(res.data);
      if (res.data.code != 200) {
        this.showError(res.data.message);
        return {};
      }
      result = this.mergeFetchedPurchases(result, res.data.data);
      lastPage = res.data.lastPage;
      page++;
    }
    return result;
  }

  /**
   * APIから入庫一覧を取得する
   */
  async function fetchPurchases(page) {
    try {
      let _params = {
        accessToken: this.accessToken,
        page: page
      };
      return await axios.get("/purchases", {
        params: _params,
      });
    } catch (e) {
      console.log("入庫一覧API　でエラー");
      console.log(e);
      let message = e.toString();
      if (message.match(/Network Error/)) {
        message = "TimeOut Error";
      }
      return {
        data: {
          code: -1,
          message: message,
        },
      };
    }
  }

  /**
   * 入庫明細をマージする
   */
  function mergeFetchedPurchases(purchases, data_list) {
    data_list.forEach((data) => {
      purchases[data.id] = {
        // nullの場合と空文字の場合があるので空文字に統一する
        customer_name: data.customer_name || ""
      }
    });
    return purchases;
  }

  /**
   * APIからdateFromまでの入庫明細を全件取得する
   * @type {function}
   * @return {json} - purchase_items 入庫明細一覧
   */
  async function loopFetchPurchasesItems(start_date, end_date) {
    let result = {};
    let page = 1;
    let lastPage = 1;
    while (page <= lastPage) {
      const res = await this.fetchPurchasesItems(page, start_date, end_date);
      console.log("[" + page + "]入庫明細一覧APIのレスポンス");
      console.log(res.data);
      if (res.data.code != 200) {
        this.showError(res.data.message);
        return {};
      }
      result = this.mergeFetchedPurchasesItems(result, res.data.data);
      lastPage = res.data.lastPage;
      page++;
    }
    return result;
  }

  /**
   * APIから入庫一覧を取得する
   */
  async function fetchPurchasesItems(page, start_date, end_date) {
    try {
      let _params = {
        accessToken: this.accessToken,
        page: page,
        end_date: end_date,
      };
      if (start_date) _params["start_date"] = start_date;
      return await axios.get("/purchases_items", {
        params: _params,
      });
    } catch (e) {
      console.log("入庫一覧API　でエラー");
      console.log(e);
      let message = e.toString();
      if (message.match(/Network Error/)) {
        message = "TimeOut Error";
      }
      return {
        data: {
          code: -1,
          message: message,
        },
      };
    }
  }

  /**
   * 入庫明細をマージする
   */
  function mergeFetchedPurchasesItems(purchase_items, data_list) {
    data_list.forEach((data) => {
      if (data.quantity == 0) return;
      if (!purchase_items[data.inventory_id]) {
        purchase_items[data.inventory_id] = [];
      }
      let unit_price = data.unit_price
      if (data.variants && data.variants.length > 0) {
        data.variants.forEach((variant) => {
          if (variant.unit_price) {
            unit_price = variant.unit_price
          }
          if (unit_price && variant.unit_snapshot && variant.unit_snapshot.factor) {
            unit_price = unit_price / variant.unit_snapshot.factor
          }
          purchase_items[data.inventory_id].push({
            purchase_date: data.purchase_date,
            quantity: variant.quantity,
            unit_price: unit_price,
            purchase_id: data.purchase_id,
            created_at: data.created_at,
            updated_at: data.updated_at,
          });
        });
      } else if (unit_price) {
        if (data.unit_snapshot && data.unit_snapshot.factor) {
          unit_price = unit_price / data.unit_snapshot.factor
        }
        purchase_items[data.inventory_id].push({
          purchase_date: data.purchase_date,
          quantity: data.quantity,
          unit_price: unit_price,
          purchase_id: data.purchase_id,
          created_at: data.created_at,
          updated_at: data.updated_at,
        });
      } else {
        purchase_items[data.inventory_id].push({
          purchase_date: data.purchase_date,
          quantity: data.quantity,
          unit_price: unit_price,
          purchase_id: data.purchase_id,
          created_at: data.created_at,
          updated_at: data.updated_at,
        });
      }
    });
    return purchase_items;
  }

  /**
   * 入庫データが入庫済みになった時刻を取得する
   * dateFrom時点の在庫数量を再計算で利用する
   * @type {function}
   */
  function create_operation_datetime_for_purchase_items(all_histories) {
    let result = {};
    all_histories.forEach((history) => {
      if (!hasValidPurchaseData(history)) return;
      const purchase_id = history["operation_content"]["purchase"]["id"];
      if (!result[purchase_id]) {
        result[purchase_id] = {};
      }
      const inventory_id = history["inventory_id"];
      if (!result[purchase_id][inventory_id]) {
        result[purchase_id][inventory_id] = history["operation_datetime"];
      }
    });
    return result;
  }

  /**
   * 出庫データが出庫済みになった時刻を取得する
   * dateFrom時点の在庫数量を再計算で利用する
   * @type {function}
   */
  function create_operation_datetime_for_deliveries(all_histories) {
    let result = {};
    all_histories.forEach((history) => {
      if (!hasValidDeliveryData(history)) return;
      const packing_slip_id = history["operation_content"]["packing_slip"]["id"];
      if (!result[packing_slip_id]) {
        result[packing_slip_id] = {};
      }
      const inventory_id = history["inventory_id"];
      if (!result[packing_slip_id][inventory_id]) {
        result[packing_slip_id][inventory_id] = history["operation_datetime"];
      }
    });
    return result;
  }

  /**
   * 入庫済みにした時刻がdateFromより後かつ入庫日がdateFrom以前の入庫データが存在する場合、
   * dateFrom時点の在庫数量を再計算する
   * @type {function}
   */
  function calc_quantity_by_past_purchase_date(inventories, purchase_items, date_from_string, history_operation_datetime_for_purchase_items, calc_errors = {}) {
    const date_from_data = new Date(date_from_string);
    inventories.forEach((inventory) => {
      inventory.quantity = Number(inventory.quantity);
      if (!purchase_items[inventory.id]) {
        return;
      }
      purchase_items[inventory.id].forEach((purchase_item) => {
        if (!purchase_item.purchase_date)  return;
        const purchase_date = new Date(purchase_item.purchase_date.substring(0, 10));

        // 変更履歴が存在しない場合
        if (!history_operation_datetime_for_purchase_items[purchase_item.purchase_id] ||
            !history_operation_datetime_for_purchase_items[purchase_item.purchase_id][inventory.id]) {

          // 再計算の対象とならない場合はスキップする
          const created_at = new Date(purchase_item.created_at.substring(0, 10));
          const updated_at = new Date(purchase_item.updated_at.substring(0, 10));          
          if ((updated_at <= date_from_data && purchase_date <= date_from_data) ||
              (date_from_data < created_at && date_from_data < purchase_date)) {
            return;
          }

          addErrorMsg(calc_errors, inventory.id, `入庫データ（purchase_id: ${purchase_item.purchase_id}）に紐づく変更履歴が存在しません。`);
          return;
        }

        const operation_datetime = new Date(history_operation_datetime_for_purchase_items[purchase_item.purchase_id][inventory.id].substring(0, 10));
        if (operation_datetime <= date_from_data && date_from_data < purchase_date) {
          inventory.quantity -= Number(purchase_item.quantity);
        } else if (purchase_date <= date_from_data && date_from_data < operation_datetime) {
          inventory.quantity += Number(purchase_item.quantity);
        }
      });
    });
    return inventories;
  }
  /**
   * 出庫登録日がdateFromより後かつ出庫日がdateFrom以前の出庫データが存在する場合、
   * dateFrom時点の在庫数量を再計算する
   * @type {function}
   */
  function calc_quantity_by_past_delivery_date(inventories, deliveries, date_from_string, history_operation_datetime_for_deliveries, calc_errors = {}) {
    const date_from_data = new Date(date_from_string);
    inventories.forEach((inventory) => {
      inventory.quantity = Number(inventory.quantity);
      if (!deliveries[inventory.id]) {
        return;
      }
      deliveries[inventory.id].forEach((delivery) => {
        if (!delivery.delivery_date)  return;
        const delivery_date = new Date(delivery.delivery_date.substring(0, 10));

        // 変更履歴が存在しない場合
        if (!history_operation_datetime_for_deliveries[delivery.packing_slip_id] ||
            !history_operation_datetime_for_deliveries[delivery.packing_slip_id][inventory.id]) {
          
          // 再計算の対象とならない場合はスキップする
          const created_at = new Date(delivery.created_at.substring(0, 10));
          const updated_at = new Date(delivery.updated_at.substring(0, 10));          
          if ((updated_at <= date_from_data && delivery_date <= date_from_data) ||
              (date_from_data < created_at && date_from_data < delivery_date)) {
            return;
          }
    
          addErrorMsg(calc_errors, inventory.id, `出庫データ（packing_slip_id: ${delivery.packing_slip_id}）に紐づく変更履歴が存在しません。`);
          return;
        }

        const operation_datetime = new Date(history_operation_datetime_for_deliveries[delivery.packing_slip_id][inventory.id].substring(0, 10));
        if (operation_datetime <= date_from_data && date_from_data < delivery_date) {
          inventory.quantity += Number(delivery.quantity);
        } else if (delivery_date <= date_from_data && date_from_data < operation_datetime) {
          inventory.quantity -= Number(delivery.quantity);
        }
      });
    });
    return inventories;
  }

  /**
   * 取消済みの変更履歴がある場合、在庫数量を再計算する
   * @type {function}
   * @param {array} - inventories 在庫一覧
   * @param {json} - histories_canceled 取消済みの変更履歴
   * @return {array} - inventories 在庫一覧
   */
  function calc_quantity_by_canceled_history(inventories, histories_canceled) {
    inventories.forEach((inventory) => {
      const inventory_id = inventory["id"];
      if (!histories_canceled[inventory_id]) return;
      histories_canceled[inventory_id].forEach((history) => {
        inventory["quantity"] += Number(history["quantity_diff"]);
      });
    });
    return inventories;
  }

  /**
   * APIからdateFromまでの出庫明細を全件取得する
   * @type {function}
   * @return {json} - purchase_items 出庫明細一覧
   */
  async function loopFetchDeliveries(start_date, end_date) {
    let result = {};
    let page = 1;
    let lastPage = 1;
    while (page <= lastPage) {
      const res = await this.fetchDeliveries(page, start_date, end_date);
      console.log("[" + page + "]出庫明細一覧APIのレスポンス");
      console.log(res.data);
      if (res.data.code != 200) {
        this.showError(res.data.message);
        return {};
      }
      result = this.mergeFetchedDeliveries(result, res.data.data);
      lastPage = res.data.lastPage;
      page++;
    }
    return result;
  }
  /**
   * 出庫明細一覧をマージする
   */
  function mergeFetchedDeliveries(deliveries, data_list) {
    data_list.forEach((data) => {
      if (data.quantity == 0) return;
      if (!deliveries[data.inventory_id]) {
        deliveries[data.inventory_id] = [];
      }
      deliveries[data.inventory_id].push({
        delivery_date: data.delivery_date,
        quantity: data.quantity,
        packing_slip_id: data.packing_slip_id,
        created_at: data.created_at,
        updated_at: data.updated_at,
      });
    });
    return deliveries;
  }
  /**
   * APIから出庫一覧を取得する
   */
  async function fetchDeliveries(page, start_date, end_date) {
    try {
      let _params = {
        accessToken: this.accessToken,
        page: page,
        end_date: end_date,
      };
      if (start_date) _params["start_date"] = start_date;
      return await axios.get("/deliveries", {
        params: _params,
      });
    } catch (e) {
      console.log("出庫一覧API　でエラー");
      console.log(e);
      let message = e.toString();
      if (message.match(/Network Error/)) {
        message = "TimeOut Error";
      }
      return {
        data: {
          code: -1,
          message: message,
        },
      };
    }
  }

  function nextday(day) {
    let nextday = new Date(day);
    nextday.setDate(nextday.getDate() + 1);
    const yyyy = nextday.getFullYear();
    const mm = ("0" + (nextday.getMonth() + 1)).slice(-2);
    const dd = ("0" + nextday.getDate()).slice(-2);
    return yyyy + "-" + mm + "-" + dd;
  }

  async function onRowClick(params) {
    console.log(params);
    const res = await axios.get("/api_base_url");
    const url = res.data.api_base_url + "/inventory_histories?common_id=" + params.row.common_id;
    window.open(url, "_blank");
  }

  /**
   * 全ての追加項目を洗い出す
   * 在庫によって追加項目が違うのでその考慮
   * @type {function}
   * @param {array} zaicoMoneyList - 在庫金額一覧
   * @return {array} - columns 追加項目一覧
   */
  function getColumns(zaicoMoneyList) {
    console.log("start getColumns");
    if (zaicoMoneyList.length == 0) return [];
    let columns = [];
    let column_names = [];
    zaicoMoneyList.forEach((zaicoMoney) => {
      Object.keys(zaicoMoney).forEach((column) => {
        if (column_names.includes(column)) return;
        column_names.push(column);
        columns.push({
          label: column,
          field: column,
        });
      });
    });
    console.log("end getColumns");
    return { columns: columns, column_names: column_names };
  }

  /**
   * CSVダウンロード
   */
  function downloadCSV(zaicoMoneyList, column_names, evnet_name) {
    this.$gtag.event(evnet_name, { user_id: this.userId });
    const config = {
      delimiter: ",", // 区切り文字
      header: true, // 1行目のキーがヘッダーになる
      newline: "\r\n", // 改行
    };

    // ヘッダーのためキー追加
    zaicoMoneyList.forEach((zaicoMoneyRow) => {
      column_names.forEach((column) => {
        if (!zaicoMoneyRow[column]) {
          zaicoMoneyRow[column] = "";
        }
      });
    });

    // 改行コードを' 'に置換
    const zaicoMoneyListTmp = this.replaceNewlineCharacter(zaicoMoneyList, column_names);

    // 区切り文字へ変換
    const zaicoMoneyCsv = Papa.unparse(zaicoMoneyListTmp, config);

    // blodへの変換
    const strArray = encoding.stringToCode(zaicoMoneyCsv);
    const convertedArray = encoding.convert(strArray, "SJIS", "UNICODE");
    const UintArray = new Uint8Array(convertedArray);
    const blob = new Blob([UintArray], { type: "text/csv" });

    // download
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "zaico_money_amount.csv";
    link.click();
  }

  /**
   * 追加項目（複数行）で使われている改行コードを' 'に置換する
   * @type {function}
   * @param {json} zaicoMoneyList - 在庫金額一覧
   * @return {array} zaicoMoneyList - 在庫金額一覧
   */
  function replaceNewlineCharacter(zaicoMoneyList, column_names) {
    console.log("start replaceNewlineCharacter");
    return zaicoMoneyList.map((zaicoMoneyRow) => {
      column_names.forEach((column) => {
        if (typeof zaicoMoneyRow[column] === "string" || zaicoMoneyRow[column] instanceof String) {
          zaicoMoneyRow[column] = zaicoMoneyRow[column].replaceAll("\r\n", " ");
        }
      });
      return zaicoMoneyRow;
    });
  }

  /**
   * 現在の在庫データをマージする
   * メインはpast_inventoriesだが、もしcurrent_inventoriesにしか存在しない在庫IDがあれば
   * 数量は0としてマージする
   */
  function merge_current_inventories(past_inventories, current_inventories) {
    console.log("start merge_current_inventories");

    let merged_inventories = {};

    past_inventories.forEach((inventory) => {
      merged_inventories[inventory.id] = inventory;
    });

    current_inventories.forEach((current_inventory) => {
      const inventory_id = current_inventory["id"];
      if (!merged_inventories[inventory_id]) {
        merged_inventories[inventory_id] = { ...current_inventory, quantity: "0" };
      }
    });

    console.log("end merge_current_inventories");
    return Object.values(merged_inventories);
  }

  function mergePurchasesItemsAndDeliveries(purchasesItemsAndHistories, deliveriesAndHistories) {
    const mergedObject = Object.assign({}, deliveriesAndHistories);
    for (const [key, value] of Object.entries(purchasesItemsAndHistories)) {
      if (!mergedObject[key]) {
        mergedObject[key] = [];
      }
      mergedObject[key] = mergedObject[key].concat(value);
    }
    return mergedObject;
  }

  function sortPurchasesItemsAndDeliveriesSorted(purchasesItemsAndDeliveries, sortType) {
    for (const key in purchasesItemsAndDeliveries) {
      purchasesItemsAndDeliveries[key].sort((a, b) => {
        const dateA = a.purchase_date || a.delivery_date;
        const dateB = b.purchase_date || b.delivery_date;
        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;

        // 日付が同じ場合
        let sortDir = 0;
        if (sortType == 'purchase_first') {
          sortDir = 1;
        } else if (sortType == 'delivery_first') {
          sortDir = -1;
        }

        if (a.purchase_date != null && b.purchase_date == null){
          // aが入庫、bが出庫の場合
          return -1 * sortDir;
        } else if (a.purchase_date == null && b.purchase_date != null){
          // aが出庫、bが入庫の場合
          return 1 * sortDir;
        }

        return 0;
      });
    }
    return purchasesItemsAndDeliveries;
  }

  function createChartDataByCustomerUsingFIFO(zaicoMoneyByCustomer) {
    // 取引先と在庫金額のペアを配列に変換し、値が0のものを除外して在庫金額で降順ソート
    const sortedZaicoMoneyByCustomer = Object.entries(zaicoMoneyByCustomer)
      .filter(([customer, sum]) => sum !== 0) // 値が0のものを除外
      .sort((a, b) => {
        if (a[0] == '') { // 取引先が未登録の場合は最後
          return 1;
        }
        if (b[0] == '') {
          return -1;
        }
        return b[1] - a[1];
      });

    // labelsとdataを設定（ソート＆フィルタリング済み）
    const labels = sortedZaicoMoneyByCustomer.map((item) => {
      if (item[0] == ''){
        return "取引先なし";
      } else {
        return item[0];
      }
    });
    const data = sortedZaicoMoneyByCustomer.map((item) => item[1]);

    return {
      titleText: "取引先別の在庫金額",
      labels: labels,
      datasets: [
        {
          data: data,
        },
      ],
    };
  }
  
  function createChartData(zaicoMoneyList, targetDate) {
    const dateKey = `${targetDate}の在庫金額`;

    // カテゴリごとに在庫金額を集計するオブジェクトを作成
    const categorySums = {};

    zaicoMoneyList.forEach((item) => {
      if (!item["category"]) {
        return;
      }

      const amount = Number(item[dateKey]) || 0;

      // カテゴリごとに在庫金額を加算
      const categories = item["category"];
      categories.split(",").forEach((category) => {
        if (!categorySums[category]) {
          categorySums[category] = 0;
        }
        categorySums[category] += amount;
      });
    });

    // カテゴリと在庫金額のペアを配列に変換し、値が0のものを除外して在庫金額で降順ソート
    const sortedCategorySums = Object.entries(categorySums)
      .filter(([category, sum]) => sum !== 0) // 値が0のものを除外
      .sort((a, b) => b[1] - a[1]);

    // labelsとdataを設定（ソート＆フィルタリング済み）
    const labels = sortedCategorySums.map((item) => item[0]);
    const data = sortedCategorySums.map((item) => item[1]);

    return {
      titleText: "カテゴリ別の在庫金額",
      labels: labels,
      datasets: [
        {
          data: data,
        },
      ],
    };
  }

  // 処理中に発生したエラー内容を在庫毎に保存
  function addErrorMsg(calc_errors, inventory_id, error_msg) {
    if(!calc_errors[inventory_id]) {
      calc_errors[inventory_id] = [];
    }
    calc_errors[inventory_id].push(error_msg);
  }

  // 何かしらの計算エラーが発生した場合は数量と在庫金額を削除
  function delete_zaico_money_due_to_errors(zaicoMoneyList, calc_errors){
    console.log("start delete_zaico_money_due_to_errors");
    return zaicoMoneyList.map((zaicoMoneyRow) => {
      if (calc_errors[zaicoMoneyRow["id"]]) {
        if (zaicoMoneyRow[this.dateFrom + "の数量"] != null) {
          zaicoMoneyRow[this.dateFrom + "の数量"] = null;
        }
        if (zaicoMoneyRow[this.dateFrom + "の在庫金額"] != null) {
          zaicoMoneyRow[this.dateFrom + "の在庫金額"] = null;
        }
        if (zaicoMoneyRow[this.dateTo + "の数量"] != null) {
          zaicoMoneyRow[this.dateTo + "の数量"] = null;
        }
        if (zaicoMoneyRow[this.dateTo + "の在庫金額"] != null) {
          zaicoMoneyRow[this.dateTo + "の在庫金額"] = null;
        }
        if (zaicoMoneyRow["Quantity at " + this.dateFrom] != null) {
          zaicoMoneyRow["Quantity at " + this.dateFrom] = null;
        }
        if (zaicoMoneyRow["Inventory Money Amount at " + this.dateFrom] != null) {
          zaicoMoneyRow["Inventory Money Amount at " + this.dateFrom] = null;
        }
        if (zaicoMoneyRow["Quantity at " + this.dateTo] != null) {
          zaicoMoneyRow["Quantity at " + this.dateTo] = null;
        }
        if (zaicoMoneyRow["Quantity Inventory Money Amount" + this.dateTo] != null) {
          zaicoMoneyRow["Quantity Inventory Money Amount" + this.dateTo] = null;
        }
      }
      return zaicoMoneyRow;
    });
  }

  /**
   * APIから在庫一覧を全件取得する
   * @type {function}
   * @param {string} - 日付 例：2020-07-20
   * @return {array} - 在庫一覧
   */
  async function loopFetchInventories(date) {
    let pastInventories = [];
    let page = 1;
    let lastPage = 1;
    while (page <= lastPage) {
      console.time("fetchInventories_" + date);
      const res = await this.fetchInventories(page, date);
      console.timeEnd("fetchInventories_" + date);
      console.log("[" + date + "][" + page + "]在庫数APIのレスポンス");
      console.log(res.data);
      if (res.data.code != 200) {
        this.showError(res.data.message);
        return [];
      }
      Array.prototype.push.apply(pastInventories, res.data.data);
      lastPage = res.data.lastPage;
      page++;
    }
    return pastInventories;
  }

  /**
   * APIからdate日時点の在庫数を取得する
   * エラーが1回までなら再実行する
   */
  async function fetchInventories(page, date) {
    let attempts = 0;
    const maxAttempts = 2; // 初回＋再試行1回

    while (attempts < maxAttempts) {
      try {
        return await axios.get("/inventories/point_in_time", {
          params: {
            target_date: date,
            accessToken: this.accessToken,
            page: page,
          },
        });
      } catch (e) {
        console.log(`${date}: 在庫一覧APIでエラー`);
        console.log(e);
        attempts += 1;
        if (attempts >= maxAttempts) {
          return {
            data: {
              code: -1,
              message: e.toString(),
            },
          };
        } else {
          console.log(`再試行します...`);
        }
      }
    }
  }

  /**
   * inventory_idがキーで仕入単価がvalueのオブジェクトを作成する
   * 入庫データの仕入単価が空の場合に利用する
   */
  function create_inventory_unit_price_json(inventories) {
    let inventory_unit_price_json = {};
    inventories.forEach((inventory) => {
      if (!inventory["仕入単価"]) return;
      inventory_unit_price_json[inventory.id] = inventory["仕入単価"].replace(/,/g, "");
    });
    return inventory_unit_price_json;
  }

  return {
    sort,
    mergePurchasesItemsAndHistories,
    mergeDeliveriesAndHistories,
    filterPurchasesItems,
    sortPurchasesItems,
    comparePurchaseIdAsc,
    filterDeliveries,
    sortDeliveries,
    createZaicoMoneyJson,
    createZaicoMoneyJsonMovingAverage,
    calc_quantity_of_inventory_created,
    loopFetchHistories,
    fetchHistories,
    loopFetchPurchasesItems,
    fetchPurchasesItems,
    fillter_histories_plus,
    fillter_histories_minus,
    fillter_histories_canceled,
    mergeFetchedPurchasesItems,
    create_operation_datetime_for_purchase_items,
    create_operation_datetime_for_deliveries,
    calc_quantity_by_past_purchase_date,
    calc_quantity_by_past_delivery_date,
    calc_quantity_by_canceled_history,
    loopFetchDeliveries,
    fetchDeliveries,
    mergeFetchedDeliveries,
    nextday,
    onRowClick,
    getColumns,
    downloadCSV,
    replaceNewlineCharacter,
    merge_current_inventories,
    mergePurchasesItemsAndDeliveries,
    sortPurchasesItemsAndDeliveriesSorted,
    createChartData,
    loopFetchInventories,
    fetchInventories,
    loopFetchPurchases,
    fetchPurchases,
    mergeFetchedPurchases,
    createChartDataByCustomerUsingFIFO,
    create_inventory_unit_price_json,
    delete_zaico_money_due_to_errors,
  };
}
